<script setup>
import {onMounted, onUnmounted, ref, watch} from 'vue';
import Button from '../../components/forms/Button.vue';
import {useRouter} from 'vue-router';
import ClientService from '@/services/ClientService';
import {formatDate, getApiBaseUrl, showErrorMessage, showSuccessMessage} from '../../helper';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import CreateUpdateClientModal from '@/components/modals/CreateUpdateClientModal.vue';
import SaveClientAddressModal from '@/components/modals/SaveClientAddressModal.vue';
import SaveClientBankAccountModal from '@/components/modals/SaveClientBankAccountModal.vue';
import SaveClientIdCardModal from '@/components/modals/SaveClientIdCardModal.vue';
import GenerateDocumentModal from '@/components/modals/GenerateDocumentModal.vue';
import documentTemplateService from '@/services/DocumentTemplateService';
import DocumentsListSimpleComponent from '@/components/documents/DocumentsListSimpleComponent.vue';
import InvestmentQuestionnaireModal from '@/components/modals/InvestmentQuestionnaireModal.vue';
import InvestmentQuestionnaireService from '@/services/InvestmentQuestionnaireService';
import {jwtDecode} from 'jwt-decode';
import CreateServiceRequirementModal from '@/components/modals/CreateServiceRequirementModal.vue';
import settingsService from '@/services/SettingsService';
import InvestmentsIndexTableComponent from '@/components/clients/InvestmentsIndexTableComponent.vue';
import CreateClientInvestmentsModal from '@/components/modals/CreateClientInvestmentsModal.vue';
import {authData, getLoggedUser, getToken} from '@/services/AuthService';
import CustomDropzone from '@/components/forms/CustomDropzone.vue';
import UserService from '@/services/UserService';
import MeetingRecordsIndex from '@/views/meeting-records/MeetingRecordsIndex.vue';
import CreateEditMeetingRecordModal from '@/components/modals/CreateEditMeetingRecordModal.vue';
import MeetingRecordService from '@/services/MeetingRecordService';
import InvestmentQuestionnairesIndex from '@/views/investment-questionnaires/InvestmentQuestionnairesIndex.vue';
import AmlQuestionnairesIndex from '@/views/aml-questionnaires/AmlQuestionnairesIndex.vue';
import AmlQuestionnaireService from '@/services/AmlQuestionnaireService';
import AmlQuestionnaireModal from '@/components/modals/AmlQuestionnaireModal.vue';
import FormsIndex from '@/views/forms/FormsIndex.vue';
import FormService from '@/services/FormService';
import CreateEditFormModal from '@/components/modals/CreateEditFormModal.vue';
import ChangeOwnerModal from '@/components/modals/ChangeOwnerModal.vue';
import IdentificationsIndex from '@/views/identifications/IdentificationsIndex.vue';
import IdentificationModal from '@/components/modals/IdentificationModal.vue';
import IdentificationService from '@/services/IdentificationService';
import ContractsIndexTableComponent from '@/components/clients/ContractsIndexTableComponent.vue';
import CreateUpdateContractModal from '@/components/modals/CreateUpdateContractModal.vue';
import {useI18n} from 'vue-i18n';
import SendAllConceptsModal from '@/components/modals/SendAllConceptsModal.vue';
import clientService from '@/services/ClientService';
import OnboardingOptionsModal from "@/components/modals/OnboardingOptionsModal.vue";
import InteractionFormService from "@/services/InteractionFormService";
import InteractionFormsTableComponent from '@/views/interaction-forms/InteractionFormsTableComponent.vue';
import SimplifiedAssistedOnboardingOptionsModal from "@/components/modals/SimplifiedAssistedOnboardingOptionsModal.vue";
import ModalDialog from "@/components/modals/ModalDialog.vue";
import DocumentService from '@/services/DocumentService';
import ChangeClientCodeModal from "@/components/modals/ChangeClientCodeModal.vue";
import UpdateClientAccountStatusModal from "@/components/modals/UpdateClientAccountStatusModal.vue";
import ClientBondsCard from "@/components/bonds/ClientBondsCard.vue";
import {bondsEnabled} from '@/utils/features';

const {t, te} = useI18n();
const router = useRouter();
const clientId = router.currentRoute.value.params.id;
const client = ref({});
const isLoading = ref(true);
const isGenerating = ref(false);
const documents = ref([]);
const loggedUser = ref({});
const interactionFormsTableComponent = ref(null);

// Barva a ikona badgeu pro každý typ adresy
const addressTypeBadge = {
  DOMICILE:       { color: 'bg-primary', icon: 'mdi-home' },
  CORRESPONDENCE: { color: 'bg-primary', icon: 'mdi-email-outline', style: 'opacity: 0.65' },
  OTHER:          { color: 'bg-secondary', icon: 'mdi-map-marker-outline' },
};

// Aktivní tab v kartě Údaje o klientovi
const activeClientTab = ref('basic');

// Iniciály pro avatar — aktualizovány watcherem při každé změně objektu klienta
const clientInitials = ref('');

watch(
  client,
  (c) => {
    if (!c || !c.client_type) return;
    const f = (c.firstname || '').charAt(0).toUpperCase();
    const s = (c.surname || '').charAt(0).toUpperCase();
    clientInitials.value = (f + s) || '?';
  },
  { deep: true },
);

// Statistiky načítané samostatně po načtení klienta
const strategiesCount = ref(null);
const contractsCount = ref(null);
const pendingSignCount = ref(null);

// Stav dropdown menu v vizitce (Bootstrap data-bs-toggle není spolehlivý v SPA)
const sendDropdownOpen = ref(false);
const manageDropdownOpen = ref(false);

/** Zavře všechny dropdowny — voláno při kliku mimo komponentu. */
function closeAllDropdowns() {
  sendDropdownOpen.value = false;
  manageDropdownOpen.value = false;
}

onUnmounted(() => {
  document.removeEventListener('click', closeAllDropdowns);
});

// --- Doklady totožnosti ---
const idCards = ref([]);
const idCardHistoryVisible = ref(false);
const idCardSaving = ref(false);

onMounted(() => {
  fetchClient();
  getLoggedUser().then(response => {
    loggedUser.value = response;
  });
  // Zavírání dropdownů při kliku mimo vizitku
  document.addEventListener('click', closeAllDropdowns);
});

function fetchClient() {
  isLoading.value = true;
  ClientService.getClient(clientId).then((response) => {
    client.value = response;
    isLoading.value = false;
  });
  // Samostatné načtení počtů pro statistiky ve vizitce
  ClientService.getClientInvestments(clientId).then(r => {
    strategiesCount.value = Array.isArray(r) ? r.length : 0;
  });
  ClientService.getClientContractsv2(clientId).then(r => {
    contractsCount.value = Array.isArray(r) ? r.length : 0;
  });
  // Počet dokumentů čekajících na podpis — filtrace na frontendu podle stavu TO_BE_SIGNED
  DocumentService.getDocumentsForClient(clientId).then(docs => {
    const list = Array.isArray(docs) ? docs : (docs?.data ?? []);
    // Stav dokumentu je zanořen v objektu d.state.state, nikoli jako d.status
    pendingSignCount.value = list.filter(d => d.state?.state === 'TO_BE_SIGNED').length;
  }).catch(() => {
    pendingSignCount.value = 0;
  });
  // Tiché načtení neaktivních dokladů — tlačítko historie se zobrazí jen pokud existují záznamy
  ClientService.getClientIdCards(clientId).then(response => {
    idCards.value = response.id_cards ?? [];
  }).catch(() => {
    idCards.value = [];
  });
}

function navigateTo(to) {
  router.push(to);
}

/** Archivuje klienta po potvrzení — pouze SUPERADMIN. */
function archiveClient() {
  const {reveal, onConfirm} = createConfirmDialog(ModalDialog, {
    title: t('clientsDetail.archive_client_confirm_title'),
    message: t('clientsDetail.archive_client_confirm_message'),
  });
  onConfirm(() => {
    ClientService.archiveClient(clientId).then(response => {
      showSuccessMessage(response.message);
      router.push({name: 'Clients'});
    });
  });
  reveal();
}

/** Obnoví archivovaného klienta po potvrzení — pouze SUPERADMIN. */
/*function restoreClient() {
  const {reveal, onConfirm} = createConfirmDialog(ModalDialog, {
    title: t('clientsDetail.restore_client_confirm_title'),
    message: t('clientsDetail.restore_client_confirm_message'),
  });
  onConfirm(() => {
    ClientService.restoreClient(clientId).then(response => {
      showSuccessMessage(response.message);
      fetchClient();
    });
  });
  reveal();
}*/

/** Přepne zobrazení panelu s historií dokladů. */
function loadIdCardHistory() {
  idCardHistoryVisible.value = !idCardHistoryVisible.value;
}

/**
 * Otevře modal pro nový doklad nebo editaci existujícího.
 * Po potvrzení uloží (nový / update) a obnoví data klienta.
 */
function openIdCardForm(idCard = null) {
  const {reveal, onConfirm} = createConfirmDialog(SaveClientIdCardModal, idCard ? {id_card: idCard} : {});
  reveal();
  onConfirm((data) => {
    idCardSaving.value = true;
    ClientService.saveClientIdCard(data, clientId, idCard?.id ?? null).then(response => {
      showSuccessMessage(response.message);
      // Fetch full client to refresh all related data (addresses, bank_accounts, id_cards)
      fetchClient();
      // Reset historie, aby se při příštím rozbalení načetla znovu aktuálně
      idCards.value = [];
      idCardHistoryVisible.value = false;
    }).finally(() => {
      idCardSaving.value = false;
    });
  });
}

function updateClient() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateUpdateClientModal, client.value ? {
    client: client.value,
  } : {});
  reveal();
  onConfirm((data) => {
    fetchClient();
  });
  onCancel(() => {
  });
}

function sendInvitation() {
  ClientService.sendInvitation(clientId).then(response => {
    showSuccessMessage(response.message);
  });
}

function sendPaymentTerms() {
  ClientService.sendPaymentTerms(clientId).then(response => {
    showSuccessMessage(response.message);
  });
}

function impersonateClient() {
  ClientService.impersonate(clientId).then(response => {
    showSuccessMessage(response.message);
  }, error => {
  });
}

function saveAddress(address = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(SaveClientAddressModal, address ? {
    address: address
  } : {});
  reveal();
  onConfirm((data) => {
    ClientService.saveClientAddress(data, clientId, address?.id ?? null).then(response => {
      showSuccessMessage(response.message);
      // Fetch full client to refresh all related data (addresses, bank_accounts, id_cards)
      fetchClient();
    });
  });
  onCancel(() => {
  });
}

function saveBankAccount(account = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(SaveClientBankAccountModal, account ? {
    bank_account: account
  } : {});

  reveal();
  onConfirm((data) => {
    ClientService.saveClientBankAccount(data, clientId, account?.id ?? null).then(response => {
      showSuccessMessage(response.message);
      // Fetch full client to refresh all related data (addresses, bank_accounts, id_cards)
      fetchClient();
    });
  });
  onCancel(() => {
  });
}

function deleteAddress(id) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('clientsDetail.address_delete'),
    message: t('clientsDetail.confirm_delete_entry'),
  });
  onConfirm(() => {
    ClientService.deleteAddress(clientId, id).then(response => {
      showSuccessMessage(response.message);
      fetchClient();
    });
  });
  reveal();
}

function deleteBankAccount(id) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('clientsDetail.bank_account_delete'),
    message: t('clientsDetail.confirm_delete_entry'),
  });
  onConfirm(() => {
    ClientService.deleteBankAccount(clientId, id).then(response => {
      showSuccessMessage(response.message);
      fetchClient();
    });
  });
  reveal();
}

function generateDocuments() {
  isGenerating.value = true;
  const {reveal, onConfirm, onCancel} = createConfirmDialog(GenerateDocumentModal, {});
  reveal();
  onConfirm((data) => {
    const body = {
      fileable_id: client.value.id,
      fileable_type: 'App\\Models\\Client',
      document_templates_ids: data['templateIds']
    };
    documentTemplateService.generateTemplates(body).then(response => {
      showSuccessMessage(response.message);
      isGenerating.value = false;
      fetchClient();
    });
  });
  onCancel(() => {
    isGenerating.value = false;
  });
}

function sendSignNotification() {

  UserService.sendSignNotification([client.value.user_id]).then(response => {
    showSuccessMessage(response.message);
  });
}

function createServiceRequirementAsClient() {
  router.push(`/service-requirements/create?client_id=${clientId}&name=${client.value.fullname}`);
}

function createServiceRequirementToClient(type = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateServiceRequirementModal, {type});
  reveal();
  onConfirm((data) => {
    const body = {
      ...data,
      client_id: clientId,
      text: '',
      state: 'REQUESTED'
    };
    void settingsService.sendRequirement(body);
  });
  onCancel(() => {
  });
}

function impersonator() {
  // getToken() vrací token aktivní pro tento panel - u impersonace jde o token
  // ze sessionStorage (localStorage nese pořád jen původní, neimpersonovanou identitu).
  const token = jwtDecode(getToken());
  if (!token || token.impersonator === null) {
    return false;
  }
  return token.impersonator;
}

function createInvestment() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateClientInvestmentsModal, {clientId: clientId});
  reveal();
  onConfirm((data) => {
    const body = {
      strategies_ids: data.selectedStrategies,
      contract_id: data.contractId
    };
    if (data.selectedStrategies.length === 0) {
      return;
    }
    ClientService.saveInvestments(clientId, body).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('investmentsSaved');
    });
  });
  onCancel(() => {
  });
}

function createContract() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateUpdateContractModal, {clientId: clientId});
  reveal();
  onConfirm((data) => {
    const body = {};
    ClientService.saveContract(clientId, data).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('contractSaved');
    }).catch(error => {
      // modal už typy, které klient má, nenabízí – sem se dostaneme typicky jen tehdy,
      // když smlouva stejného typu mezitím vznikla jinou cestou (BE vrátí 422)
      showErrorMessage(error?.message);
    });
  });
  onCancel(() => {
  });
}

function addInvestmentQuestionnaire(investmentQuestionnaire = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(InvestmentQuestionnaireModal, {investmentQuestionnaire, prefillObject: {client: client.value}, clientId: Number(clientId)});
  reveal();
  onConfirm((data) => {
    data.client_id = clientId;
    data.creator_id = authData.loggedUser.id;
    InvestmentQuestionnaireService.saveQuestionnaire(client.value.id, data).then(response => {
      showSuccessMessage(response.message);
      isGenerating.value = false;
      fetchClient();
    });
  });
  onCancel(() => {
  });
}

function addIdentification(identification = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(IdentificationModal, {identification, prefillObject: {client: client.value}});
  reveal();
  onConfirm((data) => {
    data.client_id = clientId;
    data.creator_id = authData.loggedUser.id;
    IdentificationService.saveIdentification(client.value.id, data).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('identificationSaved');
    });
  });
  onCancel(() => {
  });
}

function addAmlQuestionnaire(amlQuestionnaire = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(AmlQuestionnaireModal, {amlQuestionnaire, prefillObject: {client: client.value}, clientId: Number(clientId)});
  reveal();
  onConfirm((data) => {
    data.client_id = clientId;
    data.creator_id = authData.loggedUser.id;
    AmlQuestionnaireService.saveQuestionnaire(client.value.id, data).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('amlQuestionnaireSaved');
    });
  });
  onCancel(() => {
  });
}

function createForm(form = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateEditFormModal, {form, prefillObject: {client: client.value}});
  reveal();
  onConfirm((data) => {
    data.client_id = clientId;
    data.creator_id = authData.loggedUser.id;
    FormService.saveForm(clientId, data).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('formSaved');
    });
  });
  onCancel(() => {
  });
}

function createMeetingRecord(meetingRecord = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(CreateEditMeetingRecordModal, {meetingRecord, prefillObject: {client: client.value}});
  reveal();
  onConfirm((data) => {
    data.client_id = clientId;
    data.creator_id = authData.loggedUser.id;
    MeetingRecordService.saveMeetingRecord(clientId, data).then(response => {
      showSuccessMessage(response.message);
      emitter.emit('meetingRecordSaved');
    });
  });
  onCancel(() => {
  });

}

function createOnboarding(start = false, selfOnboarding = false) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(OnboardingOptionsModal, {clientId: client.value.id});
  reveal();
  onConfirm((data) => {
    let body = {
      config: data,
      client_id: clientId,
      start,
      is_self_onboarding: selfOnboarding
    }
    InteractionFormService.save(body).then(response => {
      if (start) {
        if (response.message) {
          showErrorMessage(response.message);
        } else {
          emitter.emit('startAssistedOnboarding', response);
        }
      } else {
        emitter.emit('onboardingSaved');
      }
      showSuccessMessage(response.message);
    });
  });
  onCancel(() => {
  });
}

function createSimplifiedOnboarding(start = false, selfOnboarding = false) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(SimplifiedAssistedOnboardingOptionsModal, {clientId: client.value.id});
  reveal();
  onConfirm((data) => {
    let body = {
      config: data,
      client_id: clientId,
      start,
      is_self_onboarding: selfOnboarding
    }
    InteractionFormService.save(body).then(response => {
      if (start) {
        if (response.message) {
          showErrorMessage(response.message);
        } else {
          emitter.emit('startAssistedOnboarding', response);
        }
      } else {
        emitter.emit('onboardingSaved');
      }
      showSuccessMessage(response.message);
    });
  });
  onCancel(() => {
  });
}

function changeOwner(account = null) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ChangeOwnerModal, client.value.intermediary_person ? {
    intermediaryPersonId: client.value.intermediary_person.id
  } : {});

  reveal();
  onConfirm((data) => {
    let body = {
      intermediary_person_id: data
    };
    ClientService.changeOwner(body, clientId).then(response => {
      showSuccessMessage(response.message);
      client.value = response.client;
    });
  });
  onCancel(() => {
  });
}

function sendAllConcepts() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(SendAllConceptsModal, {clientId: clientId});
  reveal();
  onConfirm(() => {
    clientService.sendAllConceptsToClient(clientId)
        .then(() => {
          showSuccessMessage(t('clientsDetail.concepts_sent'));
          fetchClient();
        });
  });
  onCancel(() => {
  });
}

function onUploadSuccess() {
  fetchClient();
}

function onUploadError() {
  console.log('error');
}

function changeClientCode() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ChangeClientCodeModal, client?.value.client_code ? {
    clientCode: client?.value.client_code
  } : {});

  reveal();
  onConfirm((data) => {
    let body = {
      client_code: data,
    }
    ClientService.changeClientCode(body, clientId).then(response => {
      if (response.client_code) {
        showSuccessMessage(response.message);
        //fetchClients();
        client.value.client_code = response.client_code;
      } else {
        showErrorMessage(response.message);
      }

    });
  });
  onCancel(() => {
  });
}

// IMA-511: barva badge podle stavu účtu v barevné škále aplikace. Běžné stavy používají
// tenantové varianty (barvy nastavuje TenantService podle brandu CZ/SK/SKCZ); read-only
// stavy záměrně zůstávají varovné (badge-warning/badge-danger ze styles.scss), aby
// nesplynuly s barvou brandu a BO/poradce na ně upozornily.
const accountStatusBadgeColor = {
  1: 'badge-tenant-primary',   // Valid
  0: 'badge-tenant-secondary', // Unapproved
  5: 'badge-warning text-dark', // Inheritance_process
  6: 'badge-warning text-dark', // Inheritance_closed
  9: 'badge-danger',           // Invalid
};

function updateAccountStatus() {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(UpdateClientAccountStatusModal, {
    accountStatus: client.value.account_status,
    accountStatusReason: client.value.account_status_reason,
    accessDeactivationDate: client.value.access_deactivation_date,
  });

  reveal();
  onConfirm((data) => {
    // Generický save endpoint vyžaduje email a pro správnou funkci unikátní validace i id/user_id
    // klienta (viz IMA-511 poznámka v ClientSaveRequest) - posíláme je beze změny spolu se stavem.
    const body = {
      id: client.value.id,
      user_id: client.value.user_id,
      email: client.value.email,
      account_status: data.account_status,
      account_status_reason: data.account_status_reason,
      access_deactivation_date: data.access_deactivation_date,
    };
    ClientService.saveClient(body, clientId).then(response => {
      showSuccessMessage(response.message);
      fetchClient();
    });
  });
  onCancel(() => {
  });
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="row">
        <div class="col-4">
          <a @click="router.back()" class="page-section__heading__back"><i class="mdi mdi-arrow-left"></i> {{ t('clientsDetail.back') }}</a>
        </div>
        <div class="col-4 text-center">
          <div class="page-section__title">{{ t('clientsDetail.client_detail') }}</div>
        </div>
        <div class="col-4"></div>
      </div>
    </div>
    <div class="page-section__content">

      <!-- Vizitka klienta — overflow:visible kvůli dropdown menu (karta má globálně overflow:hidden) -->
      <div class="card mb-4" style="overflow: visible;">
        <div class="card-body py-3">
          <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">

            <!-- Levá část: avatar + jméno + badge + kód + statistiky -->
            <div class="d-flex align-items-start gap-3">
              <!-- Kruhový avatar s iniciálami klienta -->
              <div class="client-avatar">{{ clientInitials }}</div>

              <div>
                <!-- Jméno + FO/PO badge + kód — vše na jednom řádku -->
                <div class="d-flex align-items-center flex-wrap gap-2 mb-3">
                  <h5 class="mb-0 fw-semibold">{{ client.fullname || client.company_name || '---' }}</h5>
                  <!-- Typ klienta je jen informativní - neutrální tenantová barva, aby nekonkuroval badge stavu -->
                  <span v-if="client.client_type === 'FO' || client.client_type === 'PO'" class="badge badge-tenant-secondary">{{ client.client_type }}</span>
                  <span v-if="client.account_status_label" class="badge" :class="accountStatusBadgeColor[client.account_status] ?? 'badge-tenant-secondary'">
                    {{ client.account_status_label }}
                  </span>
                  <button v-if="['ADMIN', 'SUPERADMIN', 'BACK_OFFICE'].includes(loggedUser?.role)" type="button" class="btn btn-link p-0 lh-1" style="font-size: 0.85rem" @click="updateAccountStatus">
                    <i class="mdi mdi-pencil"/>
                  </button>
                  <span class="text-muted small">
                    {{ t('clientsDetail.client_code') }} <strong class="text-body">{{ client.client_code ?? '---' }}</strong>
                  </span>
                  <button v-if="!impersonator()" type="button" class="btn btn-link p-0 lh-1" style="font-size: 0.85rem" @click="changeClientCode">
                    <i class="mdi mdi-pencil"/>
                  </button>
                </div>

                <!-- Statistiky jako secondary badges — načteny samostatnými API voláními -->
                <div class="d-flex flex-wrap gap-2">
                  <span class="badge bg-secondary fw-normal">
                    {{ t('clientsDetail.strategies') }}: {{ strategiesCount ?? '---' }}
                  </span>
                  <span class="badge bg-secondary fw-normal">
                    {{ t('clientsDetail.contracts') }}: {{ contractsCount ?? '---' }}
                  </span>
                  <span class="badge bg-secondary fw-normal">
                    {{ t('clientsDetail.header_pending_sign') }}: {{ pendingSignCount ?? '---' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Pravá část: datum + místo narození, občanství, rodné číslo — stejná barva textu -->
            <div class="text-end small text-muted">
              <div class="mb-1">
                {{ client.birth_date ? formatDate(client.birth_date, 'd. m. Y') : '---' }}
                <span v-if="client.birth_place"> · {{ client.birth_place }}</span>
              </div>
              <div class="mb-1">{{ client.citizenship ?? '---' }}</div>
              <div>{{ client.personal_id ?? '---' }}</div>
            </div>

          </div>

          <!-- Banner: archivovaný klient — viditelný pro SUPERADMIN -->
<!--          <div v-if="client.deleted_at" class="alert alert-warning d-flex align-items-center justify-content-between mt-3 mb-0 py-2 px-3">
            <span><i class="mdi mdi-archive-outline me-2"/>{{ t('clientsDetail.archived_banner') }}</span>
            <button
              v-if="loggedUser?.role === 'SUPERADMIN'"
              type="button"
              class="btn btn-sm btn-success"
              @click="restoreClient()"
            >
              <i class="mdi mdi-restore me-1"/>{{ t('clientsDetail.restore_client') }}
            </button>
          </div>-->

          <!-- IMA-511: klient je read-only (dědické řízení / neaktivní) - informace pro BO/poradce -->
          <div v-if="client.is_read_only" class="alert alert-warning d-flex align-items-center mt-3 mb-0 py-2 px-3">
            <i class="mdi mdi-lock-outline me-2"/>
            <span>
              {{ t('clientsDetail.read_only_banner', {status: client.account_status_label}) }}
              <template v-if="client.account_status_reason"> — {{ client.account_status_reason }}</template>
              <template v-if="client.access_deactivation_date">
                ({{ t('clientsDetail.access_deactivation_date') }}: {{ formatDate(client.access_deactivation_date, 'd. m. Y') }})
              </template>
            </span>
          </div>

          <!-- Akční tlačítka -->
          <div v-if="!impersonator()" class="d-flex flex-wrap gap-2 pt-3 mt-3 border-top vizitka-actions">
            <!-- Přihlásit se za klienta — primary barva -->
            <button type="button" class="btn btn-primary btn-sm" @click="impersonateClient()">
              <i class="mdi mdi-account-arrow-right me-1"/>{{ t('clientsDetail.impersonate_as_client') }}
            </button>

            <!-- Založit účet — primary barva, stejná jako Přihlásit se za klienta -->
            <button type="button" class="btn btn-primary btn-sm" :disabled="!interactionFormsTableComponent?.showCreateButton" @click="createSimplifiedOnboarding(true)">
              <i class="mdi mdi-plus me-1"/>{{ t('clientsDetail.create_account') }}
            </button>

            <!-- Dropdown: Odeslat — Vue-managed, bez data-bs-toggle -->
            <div class="dropdown" :class="{ show: sendDropdownOpen }">
              <button
                class="btn btn-outline-secondary btn-sm dropdown-toggle"
                type="button"
                :aria-expanded="sendDropdownOpen"
                @click.stop="sendDropdownOpen = !sendDropdownOpen; manageDropdownOpen = false"
              >
                <i class="mdi mdi-send-outline me-1"/>{{ t('clientsDetail.header_send') }}
              </button>
              <ul class="dropdown-menu" :class="{ show: sendDropdownOpen }">
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="sendDropdownOpen = false; createServiceRequirementAsClient()">
                    {{ t('clientsDetail.submit_service_request_by_client') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="sendDropdownOpen = false; createServiceRequirementToClient()">
                    {{ t('clientsDetail.submit_service_request_to_client') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="sendDropdownOpen = false; sendInvitation()">
                    {{ t('clientsDetail.send_documentation') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="sendDropdownOpen = false; sendPaymentTerms()">
                    {{ t('clientsDetail.send_payment_details') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="sendDropdownOpen = false; sendAllConcepts()">
                    {{ t('clientsDetail.send_all_concepts') }}
                  </a>
                </li>
              </ul>
            </div>

            <!-- Dropdown: Spravovat — Vue-managed, bez data-bs-toggle -->
            <div class="dropdown" :class="{ show: manageDropdownOpen }">
              <button
                class="btn btn-outline-secondary btn-sm dropdown-toggle"
                type="button"
                :aria-expanded="manageDropdownOpen"
                @click.stop="manageDropdownOpen = !manageDropdownOpen; sendDropdownOpen = false"
              >
                <i class="mdi mdi-cog-outline me-1"/>{{ t('clientsDetail.header_manage') }}
              </button>
              <ul class="dropdown-menu" :class="{ show: manageDropdownOpen }">
                <li v-if="loggedUser?.role === 'ADMIN' || loggedUser?.role === 'SUPERADMIN'">
                  <a class="dropdown-item" href="#" @click.prevent="manageDropdownOpen = false; changeOwner()">
                    {{ t('clientsDetail.change_owner') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="manageDropdownOpen = false; addIdentification()">
                    {{ t('clientsDetail.request_identification') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="manageDropdownOpen = false; createOnboarding(false, true)">
                    {{ t('clientsDetail.service_actions') }}
                  </a>
                </li>
                <!-- Archivace klienta — pouze SUPERADMIN, pouze pokud klient není archivován -->
                <li v-if="loggedUser?.role === 'SUPERADMIN' && !client.deleted_at">
                  <a class="dropdown-item text-danger" href="#" @click.prevent="manageDropdownOpen = false; archiveClient()">
                    <i class="mdi mdi-archive-outline me-1"/>{{ t('clientsDetail.archive_client') }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

        </div>
      </div><!-- /.vizitka -->

      <div class="mb-4">
        <div class="row">
          <div class="col-12 col-md-6">
            <!-- Základní údaje klienta — všechny sekce jako fieldsety uvnitř jedné karty -->
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.section_client_data') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button v-if="!impersonator()" :label="t('clientsDetail.edit')" @click="updateClient()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">

                <!-- Taby — navigace -->
                <ul class="nav nav-tabs mb-3">
                  <li class="nav-item">
                    <a class="nav-link ps-2 pe-3" :class="{ active: activeClientTab === 'basic' }" href="#" @click.prevent="activeClientTab = 'basic'">{{ t('clientsDetail.tab_basic') }}</a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link px-3" :class="{ active: activeClientTab === 'personal' }" href="#" @click.prevent="activeClientTab = 'personal'">{{ t('clientsDetail.tab_personal') }}</a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link px-3" :class="{ active: activeClientTab === 'tax' }" href="#" @click.prevent="activeClientTab = 'tax'">{{ t('clientsDetail.section_tax_residence') }}</a>
                  </li>
                  <li class="nav-item">
                    <a class="nav-link px-3" :class="{ active: activeClientTab === 'employment' }" href="#" @click.prevent="activeClientTab = 'employment'">{{ t('clientsDetail.tab_employment') }}</a>
                  </li>
                  <li v-if="client.client_type === 'PO'" class="nav-item">
                    <a class="nav-link ps-3 pe-2" :class="{ active: activeClientTab === 'representative' }" href="#" @click.prevent="activeClientTab = 'representative'">{{ t('clientsDetail.section_representative') }}</a>
                  </li>
                </ul>

                <!-- Tab: Základní -->
                <div v-if="activeClientTab === 'basic'" class="row">
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.client_type') }}</span>
                        <span class="info-table__value col-12">{{ client.client_type ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.client_code') }}</span>
                        <span class="info-table__value col-12 d-flex justify-content-between">{{ client.client_code ?? '---' }}<button v-if="!impersonator()" type="button" class="btn btn-link" @click="changeClientCode"><i class="mdi mdi-pencil"/></button></span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.title_before_name') }}</span>
                        <span class="info-table__value col-12">{{ client.title_before ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.title_behind_name') }}</span>
                        <span class="info-table__value col-12">{{ client.title_after ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.name') }}</span>
                        <span class="info-table__value col-12">{{ client.fullname ?? '---' }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.email') }}</span>
                        <span class="info-table__value col-12">{{ client.email ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.phone_number') }}</span>
                        <span class="info-table__value col-12">{{ client.phone ?? '---' }}</span>
                      </div>
                      <!-- Název společnosti se zobrazuje pouze u PO — fyzická osoba jej nevyplňuje -->
                      <div v-if="client.client_type === 'PO'" class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.company_name') }}</span>
                        <span class="info-table__value col-12">{{ client.company_name ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.referral_code') }}</span>
                        <span class="info-table__value col-12">{{ client.referral_code ?? '---' }}</span>
                      </div>
                      <!-- Firemné polia — zobrazujú sa len keď ich backend posiela (SK+PO) -->
                      <template v-if="'ico' in client">
                        <div class="info-table__row row">
                          <span class="info-table__label col-12">{{ t('clientsDetail.ico') }}</span>
                          <span class="info-table__value col-12">{{ client.ico ?? '---' }}</span>
                        </div>
                        <div class="info-table__row row">
                          <span class="info-table__label col-12">{{ t('clientsDetail.tax_number') }}</span>
                          <span class="info-table__value col-12">{{ client.tax_number ?? '---' }}</span>
                        </div>
                        <div class="info-table__row row">
                          <span class="info-table__label col-12">{{ t('clientsDetail.ic_dph') }}</span>
                          <span class="info-table__value col-12">{{ client.ic_dph ?? '---' }}</span>
                        </div>
                        <div class="info-table__row row">
                          <span class="info-table__label col-12">{{ t('clientsDetail.registry_entry') }}</span>
                          <span class="info-table__value col-12">{{ client.registry_entry ?? '---' }}</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Tab: Osobní -->
                <div v-if="activeClientTab === 'personal'" class="row">
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.personal_id') }}</span>
                        <span class="info-table__value col-12">{{ client.personal_id ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.birth_date') }}</span>
                        <span class="info-table__value col-12">{{ client.birth_date ? formatDate(client.birth_date, 'd. m. Y') : '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.birth_place') }}</span>
                        <span class="info-table__value col-12">{{ client.birth_place ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.gender') }}</span>
                        <span class="info-table__value col-12">{{ te('clientsDetail.' + client?.gender) ? t('clientsDetail.' + client?.gender) : '---' }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.marital_status') }}</span>
                        <span class="info-table__value col-12">{{ te('clientsDetail.' + client?.marital_status) ? t('clientsDetail.' + client?.marital_status) : '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.citizenship') }}</span>
                        <span class="info-table__value col-12">{{ client.citizenship ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.nationality') }}</span>
                        <span class="info-table__value col-12">{{ client.nationality ?? '---' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tab: Daně -->
                <div v-if="activeClientTab === 'tax'" class="row">
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.tax_residence') }}</span>
                        <span class="info-table__value col-12">{{ client.tax_residence ?? '---' }}</span>
                      </div>
                      <!-- TIN shown only when backend does not send company_fields (FO or CZ branch) -->
                      <div v-if="!('ico' in client)" class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.tax_number') }}</span>
                        <span class="info-table__value col-12">{{ client.tax_number ?? '---' }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.not_US_tax_residence') }}</span>
                        <span class="info-table__value col-12">{{ client.not_US_tax_residence ? 'Ano' : 'Ne' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tab: Zaměstnání -->
                <div v-if="activeClientTab === 'employment'" class="row">
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.employer_type') }}</span>
                        <span class="info-table__value col-12">{{ te('clientsDetail.employer_type_' + client.employer_type) ? t('clientsDetail.employer_type_' + client.employer_type) : (client.employer_type ?? '---') }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.occupation') }}</span>
                        <span class="info-table__value col-12">{{ client.occupation ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.note') }}</span>
                        <span class="info-table__value col-12">{{ client.note ?? '---' }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.processing_fee_for_all_portfolios') }}</span>
                        <span class="info-table__value col-12">{{ client.processing_fee_for_all_portfolios ? 'Ano' : 'Ne' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.processing_fee') }}</span>
                        <span class="info-table__value col-12">{{ client.processing_fee ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.active_access') }}</span>
                        <span class="info-table__value col-12">{{ client.active_access ? 'Ano' : 'Ne' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Tab: Zmocněnec (pouze PO) -->
                <div v-if="activeClientTab === 'representative' && client.client_type === 'PO'" class="row">
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_title_before') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_title_before ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_title_after') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_title_after ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_firstname') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_firstname ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_surname') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_surname ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_email') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_email ?? '---' }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <div class="info-table">
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_phone') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_phone ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_personal_id') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_personal_id ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_birth_date') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_birth_date ? formatDate(client.representative_birth_date, 'd. m. Y') : '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_birth_place') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_birth_place ?? '---' }}</span>
                      </div>
                      <div class="info-table__row row">
                        <span class="info-table__label col-12">{{ t('clientsDetail.representative_position') }}</span>
                        <span class="info-table__value col-12">{{ client.representative_position ?? '---' }}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div><!-- /.card-body -->
            </div><!-- /.card Základní údaje -->

          </div><!-- /.col-12 col-md-6 -->
          <div class="col-12 col-md-6">
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <div class="row">
                      <div class="col">
                        <div class="card-header__title">
                          {{ t('clientsDetail.addresses') }}
                        </div>
                      </div>
                      <div class="col text-end">
                        <div class="card-header__action">
                          <Button v-if="!impersonator()" :label="t('clientsDetail.add_address')" @click="saveAddress()"/>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">
                    <div v-if="client.addresses?.length">
                      <div class="info-table info-table--list">
                        <div class="info-table__row row" v-for="(address, index) in client.addresses" :key="index">
                          <span class="info-table__value col-10">
                            {{ address.label }}
                            <span :class="['badge', 'me-2', addressTypeBadge[address.type]?.color ?? 'bg-secondary']" :style="addressTypeBadge[address.type]?.style">
                              <i :class="['mdi', addressTypeBadge[address.type]?.icon ?? 'mdi-map-marker-outline']"/>
                              <span class="ms-2">{{ te('clientsDetail.' + address.type) ? t('clientsDetail.' + address.type) : address.type }}</span>
                            </span>
                          </span>
                          <div class="info-table__action col-2">
                            <!-- <Button icon="'mdi mdi-eye'" @click="saveAddress(address)" class="btn-link"/> -->
                            <button v-if="!impersonator()" type="button" class="btn btn-link" @click="saveAddress(address)"><i class="mdi mdi-eye"/></button>
                            <button v-if="!impersonator()" type="button" class="btn btn-link" @click="deleteAddress(address.id)"><i class="mdi mdi-trash-can-outline"/></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <div class="row">
                      <div class="col">
                        <div class="card-header__title">
                          {{ t('clientsDetail.bank_accounts') }}
                        </div>
                      </div>
                      <div class="col text-end">
                        <div class="card-header__action">
                          <Button v-if="!impersonator()" :label="t('clientsDetail.add_bank_account')" @click="saveBankAccount()"/>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">
                    <div v-if="client.bank_accounts?.length">
                      <div class="info-table info-table--list">
                        <div class="info-table__row row" v-for="(bank_account, index) in client.bank_accounts" :key="index">
                          <div class="info-table__value col-10">
                            <span>{{ bank_account.label }}</span>
                            <span v-if="bank_account.bank_account_owner" class="text-muted ms-2">{{ bank_account.bank_account_owner }}</span>
                            <span class="ms-2">
                            <span v-for="currency in bank_account.currencies" class="badge bg-secondary">{{ currency }}</span>
                          </span>
                            <span v-if="bank_account.is_primary" class="badge bg-primary ms-2">{{ t('clientsDetail.primary') }}</span>
                          </div>
                          <div class="info-table__action col-2">
                            <button type="button" class="btn btn-link" @click="saveBankAccount(bank_account)"><i class="mdi mdi-eye"/></button>
                            <button v-if="!impersonator()" type="button" class="btn btn-link" @click="deleteBankAccount(bank_account.id)"><i class="mdi mdi-trash-can-outline"/></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Doklad totožnosti — samostatná karta s historií -->
            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <div class="row">
                      <div class="col">
                        <div class="card-header__title">{{ t('clientsDetail.section_id_card') }}</div>
                      </div>
                      <div class="col text-end">
                        <div class="card-header__action">
                          <Button v-if="!impersonator()" :label="t('clientsDetail.id_card_new')" @click="openIdCardForm()"/>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="card-body">

                    <!-- Seznam aktivních dokladů -->
                    <div v-if="client?.id_cards?.length">
                      <div class="info-table info-table--list">
                        <div class="info-table__row row" v-for="card in client.id_cards" :key="card.id">
                          <div class="info-table__value col-10">
                            <!-- Typ dokladu jako hlavní popis, číslo jako doplněk -->
                            <span>{{ te('clientsDetail.' + card.id_card_type) ? t('clientsDetail.' + card.id_card_type) : card.id_card_type }}</span>
                            <span v-if="card.id_card_number" class="text-muted ms-2">{{ card.id_card_number }}</span>
                            <span v-if="card.id_card_validity" class="text-muted ms-2 small">{{ t('clientsDetail.id_card_validity_to') }} {{ formatDate(card.id_card_validity, 'd. m. Y') }}</span>
                          </div>
                          <div class="info-table__action col-2">
                            <button v-if="!impersonator()" type="button" class="btn btn-link" @click="openIdCardForm(card)"><i class="mdi mdi-pencil"/></button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else-if="!isLoading" class="text-muted small">{{ t('clientsDetail.no_records') }}</div>

                    <!-- Tlačítko pro historii — zobrazí se jen pokud existují neaktivní doklady -->
                    <div v-if="idCards.length > 0" class="pt-3 mt-3 border-top">
                      <button type="button" class="btn btn-link p-0" @click="loadIdCardHistory()">
                        {{ idCardHistoryVisible ? t('clientsDetail.id_card_history_hide') : t('clientsDetail.id_card_history_show') }}
                      </button>
                    </div>

                    <!-- Historie neaktivních dokladů -->
                    <div v-if="idCardHistoryVisible" class="mt-3">
                      <h6 class="fw-semibold mb-2">{{ t('clientsDetail.id_card_history') }}</h6>
                      <div v-for="card in idCards" :key="card.id" class="border rounded p-2 mb-2 small">
                        <div class="mb-1">
                          <span class="fw-semibold">{{ formatDate(card.created_at, 'd. m. Y H:i') }}</span>
                        </div>
                        <div class="row">
                          <span class="col-6 text-muted">{{ t('clientsDetail.id_card_type') }}</span>
                          <span class="col-6">{{ te('clientsDetail.' + card.id_card_type) ? t('clientsDetail.' + card.id_card_type) : (card.id_card_type ?? '---') }}</span>
                          <span class="col-6 text-muted">{{ t('clientsDetail.id_card_number') }}</span>
                          <span class="col-6">{{ card.id_card_number ?? '---' }}</span>
                          <span class="col-6 text-muted">{{ t('clientsDetail.id_card_issued_by') }}</span>
                          <span class="col-6">{{ card.id_card_issued_by ?? '---' }}</span>
                          <span class="col-6 text-muted">{{ t('clientsDetail.id_card_issue_date') }}</span>
                          <span class="col-6">{{ card.id_card_issue_date ? formatDate(card.id_card_issue_date, 'd. m. Y') : '---' }}</span>
                          <span class="col-6 text-muted">{{ t('clientsDetail.id_card_validity') }}</span>
                          <span class="col-6">{{ card.id_card_validity ? formatDate(card.id_card_validity, 'd. m. Y') : '---' }}</span>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div><!-- /.card Doklad totožnosti -->
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.assisted_onboardings') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button v-if="!impersonator()" :disabled="!interactionFormsTableComponent?.showCreateButton" :label="t('clientsDetail.create')" @click="createSimplifiedOnboarding(true)"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <InteractionFormsTableComponent ref="interactionFormsTableComponent" v-if="!isLoading" :entityId="client.id" />
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.contracts') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button v-if="!impersonator()" :label="t('clientsDetail.add_contract')" @click="createContract()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <ContractsIndexTableComponent v-if="!isLoading" :entityId="client.id"/>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.strategies') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button v-if="!impersonator()" :label="t('clientsDetail.add_strategy')" @click="createInvestment()" :disabled="isGenerating"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <InvestmentsIndexTableComponent v-if="!isLoading" :entityId="client.id"/>
              </div>
            </div>
          </div>
        </div>
        <!-- IMA-517 fáze 7: dluhopisy klienta (stejný přehled jako na dashboardu klienta); ne v B.I.G. Invest SK/CZ -->
        <div class="row mt-3" v-if="!isLoading && bondsEnabled(loggedUser)">
          <div class="col-12">
            <!-- „Nakoupit dluhopisy“ = nový asistovaný onboarding s kalkulačkou (strategie a dluhopisy) -->
            <ClientBondsCard :entityId="client.id" :can-buy="!impersonator() && !!interactionFormsTableComponent?.showCreateButton"
                             @buy="createSimplifiedOnboarding(true)"/>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.investment_questionnaires') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button :label="t('clientsDetail.add_questionnaires')" @click="addInvestmentQuestionnaire()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <InvestmentQuestionnairesIndex v-if="!isLoading" :clientId="client.id"/>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.identifications') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button :label="t('clientsDetail.add_identification')" @click="addIdentification()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <IdentificationsIndex v-if="!isLoading" :clientId="client.id"/>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.aml_questionnaires') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button :label="t('clientsDetail.add_questionnaires')" @click="addAmlQuestionnaire()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <AmlQuestionnairesIndex v-if="!isLoading" :clientId="client.id"/>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.forms') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button :label="t('clientsDetail.add_form')" @click="createForm()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <FormsIndex v-if="!isLoading" :clientId="client.id"/>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">
              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.meeting_records2') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <Button :label="t('clientsDetail.add_record')" @click="createMeetingRecord()"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <MeetingRecordsIndex v-if="!isLoading" :clientId="client.id"/>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-3">
          <div class="col-12">
            <div class="card">

              <div class="card-header">
                <div class="row">
                  <div class="col">
                    <div class="card-header__title">
                      {{ t('clientsDetail.documents') }}
                    </div>
                  </div>
                  <div class="col text-end">
                    <div class="card-header__action">
                      <!-- tmp hide button to send notification to sign documents -->
                      <Button v-if="false && !impersonator()" :label="t('clientsDetail.send_notification_for_signature_documents')" @click="sendSignNotification()" class="me-2"/>
                      <Button v-if="!impersonator()" :label="isGenerating ? t('clientsDetail.generating_documents')  : t('clientsDetail.generate_documents')" @click="generateDocuments()" :disabled="isGenerating"/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-body">
                <CustomDropzone v-if="!impersonator()" :dropzone-options-input="{
                  url: getApiBaseUrl() + '/api/documents/upload',
                  acceptedFiles: '.pdf, .jpg, .jpeg, .png',
                  maxFiles: 20,
                  uploadMultiple: true,
                  paramName: 'attachments',
                  params: {
                    'documentable_type': 'App\\Models\\Client',
                    'documentable_id': clientId
                  },
                  init() {
                    this.on('sendingmultiple', function (file, xhr, formData) {
                      formData.append('documentable_type', 'App\\Models\\Client');
                      formData.append('documentable_id', clientId);
                    });
                  }
                }" :upload-success="onUploadSuccess" :upload-error="onUploadError"/>
                <DocumentsListSimpleComponent v-if="!isLoading" :entityId="client.id"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Avatar klienta s iniciálami */
.client-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--primaryColor);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 600;
  flex-shrink: 0;
  letter-spacing: 0.05em;
}

/* Popisek statistiky pod hodnotou */
.client-stat-label {
  font-size: 0.7rem;
  white-space: nowrap;
}

/* Přepis globálního .btn (height: 50px, min-width: 200px !important) pro akční tlačítka vizitky */
.vizitka-actions :deep(.btn),
.vizitka-actions button.btn {
  height: auto !important;
  line-height: 1.5 !important;
  min-width: 0 !important;
  padding: 0.3rem 0.75rem !important;
}

/* Větší mezery mezi položkami v dropdownech vizitky */
.vizitka-actions :deep(.dropdown-item),
.vizitka-actions .dropdown-item {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

/* Underline styl tabů — bez ohraničení kolem, jen spodní čára pod pásem */
:deep(.nav-tabs) {
  border-bottom: 1px solid var(--bs-border-color);
}
:deep(.nav-tabs .nav-link) {
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px; /* překryje spodní border nav-tabs */
  background: transparent;
  color: inherit;
  border-radius: 0;
}
:deep(.nav-tabs .nav-link.active) {
  border-bottom-color: var(--primaryColor);
  color: var(--primaryColor);
  background: transparent;
  font-weight: 500;
}
:deep(.nav-tabs .nav-link:hover:not(.active)) {
  border-bottom-color: var(--bs-border-color);
  color: inherit;
}
</style>

